import { useCallback, useRef, useState } from "react";
import Box from "@mui/material/Box";

/*
 * 우측 패널의 폭 드래그 — 손잡이 하나와 그 손잡이가 움직이는 폭.
 *
 * 같은 코드가 여러 벌 있었다(고급 필터 패널 / 워크플로우 패널 / 화면마다 복사돼 있던 useEffect).
 * 규칙 — 왼쪽으로 끌면 넓어진다, 최소 폭, 부모 행의 70% 상한 — 이 한 곳에만 있어야
 * 나란히 선 패널들이 같은 감으로 움직인다.
 *
 * 폭은 시작 시점 기준의 **증분**으로 잰다. 행의 오른쪽 끝(rect.right)에서 빼는 방식은
 * 그 행을 가리키는 ref 를 호출부가 따로 넘겨야 한다 — 증분은 그런 게 필요 없다.
 *
 *   const { width, resizing, nodeRef, startResize } = useResizableWidth({ defaultWidth: 372 });
 *   <>
 *     <ResizeHandle onMouseDown={startResize} active={resizing} />
 *     <Box ref={nodeRef} sx={{ width }}>…</Box>   ← 부모는 flex row 여야 한다
 *   </>
 */

export const PANEL_MIN_WIDTH = 320;
export const PANEL_DEFAULT_WIDTH = 420;

const useResizableWidth = ({
  defaultWidth = PANEL_DEFAULT_WIDTH,
  minWidth = PANEL_MIN_WIDTH,
  maxRatio = 0.7,
} = {}) => {
  const [width, setWidth] = useState(defaultWidth);
  const [resizing, setResizing] = useState(false);
  const nodeRef = useRef(null);

  const startResize = useCallback(event => {
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = nodeRef.current?.getBoundingClientRect().width ?? defaultWidth;
    const rowWidth = nodeRef.current?.parentElement?.getBoundingClientRect().width ?? window.innerWidth;
    /* 부모 행의 70% 까지만 — 본문(그리드)이 손톱만 해지는 것을 막는다 */
    const maxWidth = Math.max(minWidth, Math.floor(rowWidth * maxRatio));

    const onMove = moveEvent => {
      // 왼쪽으로 끌면 넓어진다 — 패널이 행의 오른쪽 끝에 있으므로
      setWidth(Math.min(maxWidth, Math.max(minWidth, startWidth + (startX - moveEvent.clientX))));
    };

    const onUp = () => {
      setResizing(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    setResizing(true);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    // 드래그 중 텍스트가 잡히거나 커서가 깜빡이지 않게
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }, [defaultWidth, minWidth, maxRatio]);

  return { width, resizing, nodeRef, startResize };
};

/*
 * 패널 왼쪽에 서는 6px 막대. 패널과 **형제로** 놓는다(패널 안에 두면 패널의 overflow 에 잘린다).
 * 좌우 여백(mx: 0.5)이 패널 사이 간격도 겸한다 — 호출부에서 따로 pl 을 주지 않는다.
 */
export const ResizeHandle = ({ onMouseDown, active, title }) => (
  <Box
    onMouseDown={onMouseDown}
    title={title}
    sx={{
      flex: "none",
      width: 6,
      mx: 0.5,
      borderRadius: 1,
      cursor: "col-resize",
      bgcolor: active ? "primary.main" : "divider",
      "&:hover": { bgcolor: "primary.light" },
    }}
  />
);

export default useResizableWidth;
